<template>
  <ion-page>
    <ion-header mode="ios">
      <ion-toolbar>
        <ion-title>Hall Wali Murid</ion-title>
      </ion-toolbar>
    </ion-header>
    <ion-content :fullscreen="true">
      <ion-refresher slot="fixed" @ionRefresh="doRefresh($event)">
        <ion-refresher-content />
      </ion-refresher>
      <ion-content class="ion-padding">
        <ion-spinner v-if="loading" name="crescent"></ion-spinner>
        <div v-if="!nisnSaved && !loading">
          <ion-item>
            <ion-label position="floating">Masukkan NISN Anak</ion-label>
            <ion-input v-model="nisnInput" placeholder="NISN"></ion-input>
          </ion-item>
          <ion-button
            style="color: white"
            expand="block"
            @click="searchStudent"
          >
            Cari Siswa
          </ion-button>
        </div>
        <div v-else>
          <p>NISN Anak: {{ nisnInput }}</p>
        </div>
        <div v-if="studentFound && !nisnSaved && !loading">
          <ion-card>
            <ion-card-header>
              <ion-card-title>{{ student.fullName }}</ion-card-title>
              <ion-card-subtitle>{{ student.kelas }}</ion-card-subtitle>
            </ion-card-header>
            <ion-card-content>
              <p>NISN: {{ student.nisn }}</p>
              <ion-button style="color: white" expand="block" @click="saveNISN">
                Simpan NISN
              </ion-button>
            </ion-card-content>
          </ion-card>
        </div>
        <div v-if="childrenUIDs.length > 0 && !loading">
          <h2>Hasil Ujian dan Kehadiran Anak</h2>
          <ion-list>
            <ion-item
              v-for="(childNISN, index) in childrenNISNs"
              :key="childNISN"
            >
              <ion-label @click="viewChildData(childrenUIDs[index])">
                NISN: {{ childNISN }}
              </ion-label>
            </ion-item>
          </ion-list>
        </div>
        <div v-if="examResults.length > 0 && !loading">
          <h2>Hasil Ujian</h2>
          <ion-list>
            <ion-item v-for="result in examResults" :key="result.jadwalId">
              <ion-label>
                <h3>Jadwal ID: {{ result.jadwalId }}</h3>
                <p>Mata Pelajaran: {{ result.mataPelajaran }}</p>
                <p>Kelas: {{ result.kelas }}</p>
                <p>
                  Nilai:
                  <ion-badge :color="result.score >= 65 ? 'success' : 'danger'">
                    {{ result.score }}
                  </ion-badge>
                </p>
                <p>Tanggal: {{ formatDate(result.createdAt) }}</p>
              </ion-label>
            </ion-item>
          </ion-list>
        </div>
      </ion-content>
      <ion-grid class="ion-padding">
        <!-- Additional content for the grid can be added here -->
      </ion-grid>
      <ion-toast
        :is-open="toast.isOpen"
        :message="toast.message"
        :duration="toast.duration"
        :color="toast.color"
        @did-dismiss="() => (toast.isOpen = false)"
      ></ion-toast>
    </ion-content>
  </ion-page>
</template>

<script setup>
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonRefresher,
  IonRefresherContent,
  IonItem,
  IonLabel,
  IonInput,
  IonButton,
  IonToast,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardSubtitle,
  IonCardContent,
  IonList,
  IonBadge,
  IonSpinner,
} from "@ionic/vue";
import { ref, onMounted } from "vue";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { firebaseApp } from "../views/firebase.js";

const db = getFirestore(firebaseApp);
const auth = getAuth(firebaseApp);
const nisnInput = ref("");
const student = ref(null);
const studentFound = ref(false);
const nisnSaved = ref(false);
const childrenNISNs = ref([]);
const childrenUIDs = ref([]);
const examResults = ref([]);
const loading = ref(false);
const toast = ref({
  isOpen: false,
  message: "",
  duration: 2000,
  color: "success",
});

const checkAndCreateParentDoc = async () => {
  const user = auth.currentUser;
  if (user) {
    const parentDocRef = doc(db, "parents", user.uid);
    const parentDoc = await getDoc(parentDocRef);
    if (!parentDoc.exists()) {
      await setDoc(parentDocRef, {
        childrenNISNs: [],
        childrenUIDs: [],
      });
    }
  } else {
    showToast("Pengguna tidak terautentikasi.", "danger");
  }
};

const fetchParentData = async () => {
  const user = auth.currentUser;
  if (user) {
    try {
      const parentDoc = await getDoc(doc(db, "parents", user.uid));
      if (parentDoc.exists()) {
        const parentData = parentDoc.data();
        childrenNISNs.value = parentData.childrenNISNs || [];
        childrenUIDs.value = parentData.childrenUIDs || [];
        if (childrenNISNs.value.length > 0) {
          nisnSaved.value = true;
          nisnInput.value = childrenNISNs.value[0]; // assuming one child per parent for simplicity
          viewChildData(childrenUIDs.value[0]);
        }
      } else {
        showToast("Data orang tua tidak ditemukan.", "danger");
      }
    } catch (error) {
      console.error("Error fetching parent data:", error);
      showToast("Terjadi kesalahan saat mengambil data orang tua.", "danger");
    }
  } else {
    showToast("Pengguna tidak terautentikasi.", "danger");
  }
};

const searchStudent = async () => {
  if (!nisnInput.value) {
    showToast("NISN tidak boleh kosong.", "danger");
    return;
  }

  const q = query(
    collection(db, "users"),
    where("nisn", "==", nisnInput.value)
  );
  const querySnapshot = await getDocs(q);

  if (!querySnapshot.empty) {
    const docSnap = querySnapshot.docs[0];
    student.value = { id: docSnap.id, ...docSnap.data() };
    studentFound.value = true;
    showToast("Siswa ditemukan.", "success");
  } else {
    student.value = null;
    studentFound.value = false;
    showToast("Siswa tidak ditemukan.", "danger");
  }
};

const saveNISN = async () => {
  const user = auth.currentUser;
  if (user && student.value) {
    await checkAndCreateParentDoc();
    try {
      const parentDoc = await getDoc(doc(db, "parents", user.uid));
      let parentData = {};
      if (parentDoc.exists()) {
        parentData = parentDoc.data();
      }

      parentData.childrenNISNs = parentData.childrenNISNs || [];
      parentData.childrenUIDs = parentData.childrenUIDs || [];

      if (!parentData.childrenNISNs.includes(student.value.nisn)) {
        parentData.childrenNISNs.push(student.value.nisn);
        parentData.childrenUIDs.push(student.value.id);

        await updateDoc(doc(db, "parents", user.uid), parentData);
        childrenNISNs.value = parentData.childrenNISNs;
        childrenUIDs.value = parentData.childrenUIDs;
        nisnSaved.value = true;
        showToast("NISN berhasil disimpan.", "success");

        // Load child data immediately
        viewChildData(student.value.id);
      } else {
        showToast("NISN sudah ada dalam daftar.", "warning");
      }
    } catch (error) {
      console.error("Error saving NISN:", error);
      showToast("Gagal menyimpan NISN.", "danger");
    }
  } else {
    showToast("Tidak ada data siswa untuk disimpan.", "danger");
  }
};

const viewChildData = async (childUID) => {
  loading.value = true;
  try {
    const resultsRef = collection(db, `examResults/${childUID}/results`);
    const querySnapshot = await getDocs(resultsRef);

    if (!querySnapshot.empty) {
      const examResultsPromises = querySnapshot.docs.map(async (docSnap) => {
        const data = docSnap.data();
        const jadwalDoc = await getDoc(doc(db, "jadwal", data.jadwalId));
        const jadwalData = jadwalDoc.exists() ? jadwalDoc.data() : {};

        const mataPelajaranDoc = await getDoc(
          doc(db, "mataPelajaran", jadwalData.mataPelajaran)
        );
        const kelasDoc = await getDoc(doc(db, "kelas", jadwalData.kelas));

        return {
          jadwalId: data.jadwalId,
          score: data.score,
          createdAt: data.createdAt,
          mataPelajaran: mataPelajaranDoc.exists()
            ? mataPelajaranDoc.data().name
            : "N/A",
          kelas: kelasDoc.exists() ? kelasDoc.data().name : "N/A",
        };
      });

      examResults.value = await Promise.all(examResultsPromises);
      console.log("Hasil Ujian dan Kehadiran:", examResults.value);
      showToast("Data siswa berhasil ditampilkan.", "success");
    } else {
      showToast("Data siswa tidak ditemukan.", "danger");
    }
  } catch (error) {
    console.error("Error fetching child data:", error);
    showToast("Gagal mengambil data siswa.", "danger");
  } finally {
    loading.value = false;
  }
};

const doRefresh = (event) => {
  setTimeout(async () => {
    await fetchParentData();
    event.target.complete();
  }, 2000);
};

const showToast = (message, color) => {
  toast.value = {
    isOpen: true,
    message,
    duration: 2000,
    color,
  };
};

const formatDate = (timestamp) => {
  if (!timestamp) return "N/A";
  const date = new Date(timestamp.seconds * 1000);
  return date.toLocaleDateString("id-ID", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

onMounted(async () => {
  await checkAndCreateParentDoc();
  fetchParentData();
});
</script>

<style scoped>
/* Custom styling if needed */
</style>
